import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import PortalLayout from "@/layouts/PortalLayout";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { useLang } from "@/context/LangContext";
import {
  ArrowLeft, Check, X, FileText, CreditCard, UserCheck, Calendar, Copy, Mail,
  Sparkles, ExternalLink, Loader2, Clock,
} from "lucide-react";

// 4-step simplified, linear, single-page application wizard.
// Admin scrolls through each stage top-to-bottom. Stages auto-complete based on
// the application status; admin can always reopen any stage to edit data.
const STAGES = [
  { id: 1, label: "Candidature", icon: Calendar },
  { id: 2, label: "Étude & cotisation", icon: FileText },
  { id: 3, label: "Validation & paiement", icon: CreditCard },
  { id: 4, label: "Membre actif", icon: UserCheck },
];

const STATUS_LABELS = {
  submitted: "Candidature reçue",
  interview_scheduled: "Entretien planifié",
  interview_completed: "Entretien terminé",
  approved: "Approuvée",
  billing_pending: "En attente facturation",
  billing_received: "Facturation reçue",
  invoice_draft: "Facture à générer",
  invoice_sent: "Lien de paiement envoyé",
  paid: "Payée",
  onboarding_scheduled: "Onboarding planifié",
  active: "Membre actif",
  rejected: "Refusée",
};

const stageFromStatus = (s) => {
  if (["submitted", "interview_scheduled", "interview_completed"].includes(s)) return 1;
  if (["approved", "billing_pending", "billing_received"].includes(s)) return 2;
  if (["invoice_draft", "invoice_sent"].includes(s)) return 3;
  if (["paid", "onboarding_scheduled", "active"].includes(s)) return 4;
  return 1;
};

function StageHeader({ id, label, icon: I, active, complete }) {
  return (
    <div className="flex items-center gap-3">
      <div className={`grid h-10 w-10 place-items-center rounded-xl font-bold ${
        complete ? "bg-emerald-100 text-emerald-700" : active ? "bg-wine-500 text-white" : "bg-neutral-100 text-neutral-400"
      }`}>
        {complete ? <Check className="h-5 w-5" /> : <I className="h-5 w-5" />}
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">Étape {id}/4</div>
        <h2 className="text-xl font-bold text-navy">{label}</h2>
      </div>
    </div>
  );
}

// Historique chronologique dérivé des timestamps présents sur la fiche.
// Ne montre que les étapes dont la date est renseignée — pas de rang fictif.
function TimelinePanel({ app, lang }) {
  const SOURCE_LABEL = {
    apply_form: "formulaire public /apply",
    prospect_conversion: "conversion depuis un prospect",
    contact_conversion: "conversion depuis un contact",
    fillout: "formulaire Fillout",
    manual: "création manuelle (admin)",
  };
  const fmt = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleString(lang || "fr", { dateStyle: "long", timeStyle: "short" });
    } catch {
      return iso;
    }
  };

  const events = [];
  if (app.submitted_at || app.created_at) {
    events.push({
      key: "received",
      icon: Calendar,
      color: "bg-blue-100 text-blue-700",
      title: "Candidature reçue",
      subtitle: `Source : ${SOURCE_LABEL[app.source] || app.source || "—"}`,
      at: app.submitted_at || app.created_at,
    });
  }
  if (app.interview_date) {
    events.push({
      key: "interview_scheduled",
      icon: Calendar,
      color: "bg-sky-100 text-sky-700",
      title: "Entretien planifié",
      subtitle: null,
      at: app.interview_date,
    });
  }
  if (app.interview_notes || app.status === "interview_completed") {
    events.push({
      key: "interview_completed",
      icon: UserCheck,
      color: "bg-sky-100 text-sky-700",
      title: "Entretien terminé",
      subtitle: app.interview_notes ? "Notes consignées" : null,
      at: app.interview_completed_at || app.interview_date,
    });
  }
  if (app.approved_at) {
    events.push({
      key: "approved",
      icon: Check,
      color: "bg-emerald-100 text-emerald-700",
      title: "Candidature validée",
      subtitle: `par ${app.approved_by_name || app.approved_by_email || "admin"}`,
      at: app.approved_at,
    });
  }
  if (app.rejected_at) {
    events.push({
      key: "rejected",
      icon: X,
      color: "bg-red-100 text-red-700",
      title: "Candidature refusée",
      subtitle: `par ${app.rejected_by_name || app.rejected_by_email || "admin"}`,
      at: app.rejected_at,
    });
  }
  if (app.fee_calculation?.validated_at) {
    events.push({
      key: "fee_validated",
      icon: FileText,
      color: "bg-amber-100 text-amber-700",
      title: "Cotisation validée",
      subtitle: app.fee_calculation.final_amount ? `${app.fee_calculation.final_amount} €` : null,
      at: app.fee_calculation.validated_at,
    });
  }
  if (app.billing_email_sent_at) {
    events.push({
      key: "billing_email",
      icon: Mail,
      color: "bg-amber-100 text-amber-700",
      title: "Email de facturation envoyé",
      subtitle: null,
      at: app.billing_email_sent_at,
    });
  }
  if (app.billing_received_at) {
    events.push({
      key: "billing_received",
      icon: FileText,
      color: "bg-amber-100 text-amber-700",
      title: "Infos de facturation reçues",
      subtitle: null,
      at: app.billing_received_at,
    });
  }
  if (app.invoice_sent_at || app.invoice_generated_at) {
    events.push({
      key: "invoice_sent",
      icon: CreditCard,
      color: "bg-violet-100 text-violet-700",
      title: "Facture générée & lien de paiement envoyé",
      subtitle: null,
      at: app.invoice_sent_at || app.invoice_generated_at,
    });
  }
  if (Array.isArray(app.reminders_log) && app.reminders_log.length > 0) {
    app.reminders_log.forEach((r, idx) => {
      events.push({
        key: `reminder_${idx}`,
        icon: Mail,
        color: "bg-rose-100 text-rose-700",
        title: `Relance paiement n°${idx + 1}`,
        subtitle: r.source === "auto" ? "Envoi automatique (cron J+7)" : `Envoyée manuellement par ${r.by_name || r.by || "admin"}`,
        at: r.at,
      });
    });
  } else if (app.last_reminder_at) {
    events.push({
      key: "reminder_last",
      icon: Mail,
      color: "bg-rose-100 text-rose-700",
      title: `Relance paiement${app.reminders_count ? ` n°${app.reminders_count}` : ""}`,
      subtitle: app.last_reminder_source === "auto" ? "Envoi automatique" : `par ${app.last_reminder_by || "admin"}`,
      at: app.last_reminder_at,
    });
  }
  if (app.paid_at) {
    events.push({
      key: "paid",
      icon: CreditCard,
      color: "bg-emerald-100 text-emerald-700",
      title: "Cotisation payée",
      subtitle: null,
      at: app.paid_at,
    });
  }
  if (app.onboarding_scheduled_at) {
    events.push({
      key: "onboarding",
      icon: UserCheck,
      color: "bg-indigo-100 text-indigo-700",
      title: "Onboarding planifié",
      subtitle: app.assigned_manager_name ? `avec ${app.assigned_manager_name}` : null,
      at: app.onboarding_scheduled_at,
    });
  }
  if (app.activated_at) {
    events.push({
      key: "active",
      icon: UserCheck,
      color: "bg-emerald-100 text-emerald-700",
      title: "Membre actif",
      subtitle: null,
      at: app.activated_at,
    });
  }

  events.sort((a, b) => (a.at || "").localeCompare(b.at || ""));

  if (events.length === 0) return null;

  return (
    <section className="mt-6 rounded-2xl border border-neutral-200 bg-white p-6" data-testid="candidate-timeline">
      <div className="flex items-center gap-2">
        <Clock className="h-4 w-4 text-wine-600" />
        <h3 className="font-serif text-lg font-semibold text-navy">Historique</h3>
        <span className="text-xs text-neutral-400">· {events.length} étape{events.length > 1 ? "s" : ""}</span>
      </div>
      <ol className="relative mt-5 space-y-5 border-l border-wine-100 pl-6">
        {events.map((e) => {
          const Ico = e.icon;
          return (
            <li key={e.key} className="relative" data-testid={`timeline-event-${e.key}`}>
              <span className={`absolute -left-[33px] grid h-6 w-6 place-items-center rounded-full ring-4 ring-white ${e.color}`}>
                <Ico className="h-3 w-3" />
              </span>
              <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
                <span className="text-sm font-semibold text-navy">{e.title}</span>
                <span className="text-[11px] text-neutral-500">{fmt(e.at)}</span>
              </div>
              {e.subtitle && <div className="mt-0.5 text-xs text-neutral-600">{e.subtitle}</div>}
            </li>
          );
        })}
      </ol>
    </section>
  );
}

export default function AdminApplicationDetail() {
  const { id } = useParams();
  const { t, lang } = useLang();
  const [app, setApp] = useState(null);
  const [suggested, setSuggested] = useState(null);
  const [feeForm, setFeeForm] = useState({ base_fee: 500, discount: 0, discount_type: "amount", final_amount: 500, notes: "" });
  const [validating, setValidating] = useState(false);
  const [interview, setInterview] = useState({ interview_date: "", interview_notes: "" });
  const [onboarding, setOnboarding] = useState({ assigned_manager_name: "Claire Admin", scheduled_at: "" });
  const [sendingEmail, setSendingEmail] = useState(false);
  const [generating, setGenerating] = useState(false);

  const load = () =>
    api.get(`/applications/${id}`).then((r) => {
      setApp(r.data);
      const fc = r.data.fee_calculation || {};
      setFeeForm({
        base_fee: fc.base_fee ?? 500,
        discount: fc.discount ?? 0,
        discount_type: fc.discount_type || "amount",
        final_amount: fc.final_amount ?? 500,
        notes: fc.notes ?? "",
      });
      if (r.data.interview_date) setInterview({ interview_date: r.data.interview_date.slice(0, 10), interview_notes: r.data.interview_notes || "" });
    }).catch(() => {});

  useEffect(() => {
    load();
    api.get(`/applications/${id}/suggested-fee`).then((r) => setSuggested(r.data)).catch(() => {});
  }, [id]);

  const setStatus = async (status) => {
    try { await api.patch(`/applications/${id}/status`, { status }); toast.success("Statut mis à jour"); load(); }
    catch { toast.error("Erreur"); }
  };
  const saveInterview = async (completed) => {
    try {
      await api.patch(`/applications/${id}/interview`, { ...interview, completed });
      toast.success(completed ? "Entretien marqué terminé" : "Entretien planifié");
      load();
    } catch { toast.error("Erreur"); }
  };
  const useSuggested = () => {
    if (!suggested) return;
    setFeeForm({ ...feeForm, base_fee: suggested.annual_fee, final_amount: suggested.suggested_amount, discount: suggested.annual_fee - suggested.suggested_amount, discount_type: "amount" });
  };

  // Sprint 87 — Recompute final_amount whenever base/discount/type changes
  const recompute = (next) => {
    const base = parseFloat(next.base_fee) || 0;
    const disc = parseFloat(next.discount) || 0;
    const type = next.discount_type || "amount";
    const final = type === "percent"
      ? Math.max(0, Math.round((base * (100 - disc) / 100) * 100) / 100)
      : Math.max(0, Math.round((base - disc) * 100) / 100);
    return { ...next, final_amount: final };
  };
  const updateFee = (patch) => setFeeForm((prev) => recompute({ ...prev, ...patch }));

  // Sprint 87 — Pre-validate the fee (admin locks the amount before sending the billing email)
  const validateFee = async () => {
    if (feeForm.final_amount <= 0) { toast.error("Le montant final doit être > 0"); return; }
    setValidating(true);
    try {
      await api.post(`/applications/${id}/billing-validate`, {
        base_fee: feeForm.base_fee,
        discount: feeForm.discount,
        discount_type: feeForm.discount_type,
        final_amount: feeForm.final_amount,
        notes: feeForm.notes,
      });
      toast.success("✅ Cotisation validée — vous pouvez envoyer l'email de facturation au candidat");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
    setValidating(false);
  };
  const sendBillingEmail = async () => {
    setSendingEmail(true);
    try {
      await api.post(`/applications/${id}/send-billing-email`, { origin_url: window.location.origin });
      toast.success("📧 Email de facturation envoyé au candidat");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
    setSendingEmail(false);
  };
  const validateAndInvoice = async () => {
    setGenerating(true);
    try {
      const { data } = await api.post(`/applications/${id}/validate-and-invoice`, {
        base_fee: feeForm.base_fee,
        discount: feeForm.discount,
        discount_type: feeForm.discount_type,
        final_amount: feeForm.final_amount,
        notes: feeForm.notes,
        origin_url: window.location.origin,
      });
      toast.success("✅ Facture créée + email de bienvenue envoyé avec lien de paiement");
      window.open(data.checkout_url, "_blank");
      load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Erreur"); }
    setGenerating(false);
  };
  const saveOnboarding = async (completed = false) => {
    try {
      await api.patch(`/applications/${id}/onboarding`, {
        assigned_manager_name: onboarding.assigned_manager_name,
        scheduled_at: onboarding.scheduled_at ? new Date(onboarding.scheduled_at).toISOString() : undefined,
        completed,
      });
      toast.success(completed ? "Membre actif ✓" : "Onboarding planifié");
      load();
    } catch { toast.error("Erreur"); }
  };

  const billingLink = `${window.location.origin}/billing/${id}`;
  const copyBilling = () => { navigator.clipboard.writeText(billingLink); toast.success("Lien copié"); };

  if (!app) return <PortalLayout sidebar="admin"><div className="py-20 text-center">Chargement...</div></PortalLayout>;

  const stage = stageFromStatus(app.status);

  return (
    <PortalLayout sidebar="admin">
      <Link to="/admin/applications" className="inline-flex items-center gap-1 text-sm font-semibold text-wine-600 hover:text-wine-700" data-testid="back-to-pipeline">
        <ArrowLeft className="h-4 w-4" /> Retour aux candidatures
      </Link>

      {/* Header — identity + global status */}
      <div className="mt-4 flex flex-col items-start justify-between gap-3 md:flex-row md:items-center">
        <div>
          <h1 className="text-3xl font-bold text-navy md:text-4xl">{app.company_name}</h1>
          <p className="mt-1 text-neutral-600">{app.contact_name} · {app.email} · {app.country}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="rounded-full bg-wine-100 px-3 py-1.5 text-xs font-bold uppercase tracking-wider text-wine-800" data-testid="app-status-badge">{STATUS_LABELS[app.status]}</span>
          <select value={app.status} onChange={(e) => setStatus(e.target.value)}
                  className="cursor-pointer rounded-full border border-neutral-300 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:border-wine-400"
                  title="Forcer le statut (admin override)" data-testid="admin-status-override">
            {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>→ {v}</option>)}
          </select>
        </div>
      </div>

      {/* Stages stepper at top — clickable to scroll */}
      <div className="mt-6 grid gap-3 md:grid-cols-4" data-testid="stages-stepper">
        {STAGES.map((s) => {
          const complete = stage > s.id;
          const active = stage === s.id;
          return (
            <a key={s.id} href={`#stage-${s.id}`} className={`rounded-xl border p-3 transition-all hover:border-wine-300 ${
              complete ? "border-emerald-200 bg-emerald-50/40" : active ? "border-wine-500 bg-wine-50/40 ring-2 ring-wine-100" : "border-neutral-200 bg-white"
            }`} data-testid={`stepper-${s.id}`}>
              <StageHeader id={s.id} label={s.label} icon={s.icon} active={active} complete={complete} />
            </a>
          );
        })}
      </div>

      {/* Chronological history derived from timestamps on the application */}
      <TimelinePanel app={app} lang={lang} />

      {/* STAGE 1 — Candidature (infos + entretien fusionnés) */}
      <section id="stage-1" className="mt-8 rounded-2xl border border-wine-100 bg-white p-6" data-testid="stage-1">
        <StageHeader id={1} label="Candidature" icon={Calendar} active={stage === 1} complete={stage > 1} />

        {/* Candidate-submitted data */}
        <div className="mt-5 grid gap-4 md:grid-cols-2">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-400">Description</div>
            <p className="mt-1 text-sm text-neutral-700">{app.description || <span className="italic text-neutral-400">—</span>}</p>
          </div>
          {(app.source || app.meeting_date || app.website || app.linkedin || app.innovation_types || app.motivation) && (
            <div className="rounded-xl border border-wine-100 bg-wine-50/30 p-3 text-xs space-y-1">
              {app.source === "fillout" && <span className="inline-flex items-center gap-1 rounded-full bg-rose-100 px-2 py-0.5 text-[10px] font-bold text-rose-700">📝 Soumise via Fillout</span>}
              {app.meeting_date && <div><strong className="text-emerald-700">📅 RDV demandé :</strong> {new Date(app.meeting_date).toLocaleString("fr", { dateStyle: "full", timeStyle: "short" })}</div>}
              {app.website && <div><strong>🌐 Site :</strong> <a href={app.website.startsWith("http") ? app.website : `https://${app.website}`} target="_blank" rel="noreferrer" className="text-wine-700 hover:underline">{app.website}</a></div>}
              {app.linkedin && <div><strong>💼 LinkedIn :</strong> <a href={app.linkedin} target="_blank" rel="noreferrer" className="text-wine-700 hover:underline">{app.linkedin}</a></div>}
              {app.innovation_types && <div><strong>💡 Innovation :</strong> {app.innovation_types}</div>}
              {app.motivation && <div><strong>🎯 Intérêt :</strong> {app.motivation}</div>}
            </div>
          )}
        </div>

        {/* Interview sub-section */}
        <div className="mt-6 rounded-xl border border-neutral-200 bg-neutral-50/50 p-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-neutral-500">Entretien (optionnel)</div>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            <input type="date" value={interview.interview_date}
                   onChange={(e) => setInterview({ ...interview, interview_date: e.target.value })}
                   className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="interview-date" />
            <div className="flex gap-2">
              <button onClick={() => saveInterview(false)} className="flex-1 rounded-full bg-blue-50 px-4 py-2 text-xs font-semibold text-blue-700 hover:bg-blue-100" data-testid="interview-schedule">Planifier</button>
              <button onClick={() => saveInterview(true)} className="flex-1 rounded-full bg-blue-500 px-4 py-2 text-xs font-semibold text-white hover:bg-blue-600" data-testid="interview-complete">Marquer terminé</button>
            </div>
          </div>
          <textarea rows={3} placeholder="Notes d'entretien..." value={interview.interview_notes}
                    onChange={(e) => setInterview({ ...interview, interview_notes: e.target.value })}
                    className="mt-3 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="interview-notes" />
        </div>

        {/* Decision */}
        <div className="mt-6 flex flex-wrap items-center gap-2 rounded-xl border border-wine-200 bg-wine-50/40 p-4">
          <div className="mr-auto text-sm font-semibold text-wine-800">Décision :</div>
          {app.status === "approved" ? (
            <div
              className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-800"
              data-testid="approved-badge"
            >
              <Check className="h-3.5 w-3.5 text-emerald-600" />
              <span>
                {t("application_validated_by")
                  .replace("{name}", app.approved_by_name || app.approved_by_email || "—")
                  .replace(
                    "{date}",
                    app.approved_at
                      ? new Date(app.approved_at).toLocaleString(lang || "fr", { dateStyle: "long", timeStyle: "short" })
                      : "—"
                  )}
              </span>
            </div>
          ) : app.status === "rejected" ? (
            <div
              className="inline-flex items-center gap-2 rounded-full border border-red-200 bg-red-50 px-4 py-2 text-xs font-semibold text-red-800"
              data-testid="rejected-badge"
            >
              <X className="h-3.5 w-3.5 text-red-600" />
              <span>
                {t("application_rejected_by")
                  .replace("{name}", app.rejected_by_name || app.rejected_by_email || "—")
                  .replace(
                    "{date}",
                    app.rejected_at
                      ? new Date(app.rejected_at).toLocaleString(lang || "fr", { dateStyle: "long", timeStyle: "short" })
                      : "—"
                  )}
              </span>
            </div>
          ) : stage > 1 ? (
            <div
              className="inline-flex items-center gap-2 rounded-full border border-emerald-200 bg-emerald-50 px-4 py-2 text-xs font-semibold text-emerald-800"
              data-testid="approved-badge"
            >
              <Check className="h-3.5 w-3.5 text-emerald-600" />
              <span>
                {t("application_validated_by")
                  .replace("{name}", app.approved_by_name || app.approved_by_email || "—")
                  .replace(
                    "{date}",
                    app.approved_at
                      ? new Date(app.approved_at).toLocaleString(lang || "fr", { dateStyle: "long", timeStyle: "short" })
                      : "—"
                  )}
              </span>
            </div>
          ) : (
            <>
              <button onClick={() => setStatus("approved")}
                      className="inline-flex items-center gap-1.5 rounded-full bg-green-500 px-5 py-2 text-xs font-semibold text-white hover:bg-green-600" data-testid="approve-app">
                <Check className="h-3.5 w-3.5" /> Approuver — passer à l&apos;étude
              </button>
              <button onClick={() => setStatus("rejected")}
                      className="inline-flex items-center gap-1.5 rounded-full bg-red-50 px-5 py-2 text-xs font-semibold text-red-700 hover:bg-red-100" data-testid="reject-app">
                <X className="h-3.5 w-3.5" /> Refuser
              </button>
            </>
          )}
        </div>
      </section>

      {/* STAGE 2 — Étude (cotisation + collecte facturation) */}
      <section id="stage-2" className="mt-6 rounded-2xl border border-wine-100 bg-white p-6" data-testid="stage-2">
        <StageHeader id={2} label="Étude & cotisation" icon={FileText} active={stage === 2} complete={stage > 2} />

        {/* Suggested fee */}
        {suggested && (
          <div className="mt-5 rounded-xl border border-wine-200 bg-gradient-to-br from-wine-50/60 to-amber-50/40 p-4" data-testid="suggested-fee-box">
            <div className="flex items-start justify-between gap-3 flex-wrap">
              <div className="text-sm">
                <div className="font-bold text-wine-800">Tarif suggéré (grille d&apos;adhésion) : <span className="text-lg">{suggested.suggested_amount}€</span></div>
                <div className="mt-0.5 text-xs text-neutral-600">{suggested.rule}</div>
                {suggested.category_name && (
                  <div className="mt-0.5 text-[11px] text-neutral-500">
                    Catégorie : <strong>{suggested.category_name}</strong>
                    {suggested.bracket_label ? ` · Tranche : ${suggested.bracket_label}` : ""}
                    {suggested.revenue_used != null ? ` · CA déclaré : ${Number(suggested.revenue_used).toLocaleString("fr-FR")}€` : ""}
                  </div>
                )}
              </div>
              <button onClick={useSuggested} disabled={!suggested.suggested_amount}
                      className="rounded-full bg-wine-500 px-4 py-1.5 text-[11px] font-semibold text-white hover:bg-wine-600 disabled:opacity-40" data-testid="use-suggested">
                <Sparkles className="mr-1 inline h-3 w-3" /> Utiliser ce tarif
              </button>
            </div>
          </div>
        )}

        {/* Fee form */}
        <div className="mt-5 grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-semibold uppercase text-neutral-500">Tarif de base (€)</label>
            <input type="number" value={feeForm.base_fee} onChange={(e) => updateFee({ base_fee: parseFloat(e.target.value) || 0 })} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="fee-base" />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <label className="text-[10px] font-semibold uppercase text-neutral-500">Remise</label>
              <div className="inline-flex rounded-full border border-neutral-200 bg-neutral-50 p-0.5 text-[10px] font-bold" data-testid="discount-type-toggle">
                <button
                  type="button"
                  onClick={() => updateFee({ discount_type: "amount" })}
                  className={`rounded-full px-2 py-0.5 ${feeForm.discount_type === "amount" ? "bg-wine-500 text-white" : "text-neutral-500"}`}
                  data-testid="discount-type-amount"
                >€</button>
                <button
                  type="button"
                  onClick={() => updateFee({ discount_type: "percent" })}
                  className={`rounded-full px-2 py-0.5 ${feeForm.discount_type === "percent" ? "bg-wine-500 text-white" : "text-neutral-500"}`}
                  data-testid="discount-type-percent"
                >%</button>
              </div>
            </div>
            <input
              type="number"
              min="0"
              max={feeForm.discount_type === "percent" ? 100 : undefined}
              value={feeForm.discount}
              onChange={(e) => updateFee({ discount: parseFloat(e.target.value) || 0 })}
              className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm"
              data-testid="fee-discount"
            />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] font-semibold uppercase text-neutral-500">Cotisation finale (€) — calculée automatiquement</label>
            <input type="number" value={feeForm.final_amount} readOnly className="mt-1 w-full rounded-xl border-2 border-wine-300 bg-wine-50/30 px-3 py-2 text-base font-bold text-wine-700 cursor-not-allowed" data-testid="fee-final" />
          </div>
          <div className="col-span-2"><textarea rows={2} placeholder="Notes admin (visible interne uniquement)..." value={feeForm.notes} onChange={(e) => setFeeForm({ ...feeForm, notes: e.target.value })} className="w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="fee-notes" /></div>
          <div className="col-span-2 flex items-center justify-between gap-3 rounded-xl border border-emerald-200 bg-emerald-50/30 p-3">
            <div className="text-xs text-emerald-800">
              {app.fee_calculation?.validated_at
                ? <>✅ Cotisation validée le {new Date(app.fee_calculation.validated_at).toLocaleDateString("fr")} — vous pouvez envoyer l&apos;email de facturation ci-dessous.</>
                : <>1️⃣ Validez d&apos;abord le montant pour débloquer l&apos;envoi de l&apos;email de facturation.</>}
            </div>
            <button
              onClick={validateFee}
              disabled={validating || feeForm.final_amount <= 0}
              className="inline-flex items-center gap-1.5 rounded-full bg-emerald-500 px-4 py-2 text-xs font-bold text-white hover:bg-emerald-600 disabled:opacity-40"
              data-testid="validate-fee-btn"
            >
              {validating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
              {app.fee_calculation?.validated_at ? "Mettre à jour la cotisation" : "Valider la cotisation"}
            </button>
          </div>
        </div>

        {/* Billing collection */}
        <div className="mt-6 rounded-xl border border-amber-200 bg-amber-50/50 p-4">
          <div className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Collecte des informations de facturation</div>
          {app.billing_info ? (
            <div className="mt-3 space-y-1 text-sm">
              <div className="inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-3 py-1 text-[11px] font-bold text-emerald-700"><Check className="h-3 w-3" /> Facturation reçue</div>
              <div className="mt-2"><span className="font-semibold">{app.billing_info.legal_company_name || app.billing_info.legal_name}</span></div>
              <div className="text-neutral-600">{app.billing_info.billing_contact_name} · {app.billing_info.billing_email}</div>
              <div className="text-neutral-600">{app.billing_info.billing_address}, {app.billing_info.postal_code} {app.billing_info.city}, {app.billing_info.country}</div>
              <div className="text-xs text-neutral-500">SIREN: {app.billing_info.siren || "—"} · VAT: {app.billing_info.vat_number || "—"}</div>
            </div>
          ) : (
            <>
              <p className="mt-2 text-xs text-amber-800">
                {app.fee_calculation?.validated_at
                  ? "2️⃣ Envoyez par email au candidat le lien sécurisé. Dès qu'il aura rempli ses infos, la facture Stripe sera générée AUTOMATIQUEMENT et il recevra le lien de paiement par email."
                  : "⚠️ Validez d'abord la cotisation ci-dessus pour débloquer l'envoi de l'email."}
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <button onClick={sendBillingEmail} disabled={sendingEmail || !app.fee_calculation?.validated_at}
                        className="inline-flex items-center gap-1.5 rounded-full bg-amber-500 px-4 py-2 text-xs font-bold text-white hover:bg-amber-600 disabled:opacity-50" data-testid="send-billing-email">
                  {sendingEmail ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Mail className="h-3.5 w-3.5" />}
                  Envoyer l&apos;email de facturation
                </button>
                <input readOnly value={billingLink}
                       className="flex-1 min-w-[200px] rounded-xl border border-neutral-200 bg-white px-3 py-2 text-xs" data-testid="billing-link" />
                <button onClick={copyBilling} className="rounded-full bg-white border border-neutral-300 px-3 py-2 text-xs font-semibold text-neutral-700 hover:bg-neutral-50" data-testid="copy-billing-link">
                  <Copy className="inline h-3 w-3" /> Copier
                </button>
              </div>
              {app.billing_email_sent_at && (
                <div className="mt-2 text-[11px] text-neutral-500">
                  Email envoyé le {new Date(app.billing_email_sent_at).toLocaleString("fr")}
                </div>
              )}
            </>
          )}
        </div>
      </section>

      {/* STAGE 3 — Validation & paiement (combo: validate + generate Stripe + welcome email) */}
      <section id="stage-3" className="mt-6 rounded-2xl border border-wine-100 bg-white p-6" data-testid="stage-3">
        <StageHeader id={3} label="Validation & paiement" icon={CreditCard} active={stage === 3} complete={stage > 3} />

        {app.stripe?.checkout_url ? (
          <div className="mt-5 space-y-3">
            <div className="flex items-center justify-between rounded-xl border border-wine-200 bg-wine-50/40 p-4 flex-wrap gap-2">
              <div>
                <div className="font-bold text-wine-700 text-lg">{app.stripe.amount}€ · {(app.stripe.status || "pending").toUpperCase()}</div>
                <div className="text-xs text-neutral-500">Session Stripe : {app.stripe.session_id?.slice(0, 24)}...</div>
                {app.welcome_email_sent_at && (
                  <div className="mt-1 text-xs text-emerald-700">✉️ Email de bienvenue envoyé le {new Date(app.welcome_email_sent_at).toLocaleString("fr")}</div>
                )}
              </div>
              <a href={app.stripe.checkout_url} target="_blank" rel="noreferrer"
                 className="inline-flex items-center gap-1.5 rounded-full bg-wine-500 px-4 py-2 text-xs font-bold text-white hover:bg-wine-600" data-testid="open-stripe">
                <ExternalLink className="h-3.5 w-3.5" /> Voir Stripe
              </a>
            </div>
            {app.stripe.status !== "paid" && (
              <button onClick={validateAndInvoice} disabled={generating} className="text-xs font-semibold text-wine-600 underline hover:text-wine-700" data-testid="regenerate-invoice">
                {generating ? "…" : "Régénérer la facture + re-envoyer l'email"}
              </button>
            )}
          </div>
        ) : (
          <>
            <p className="mt-4 text-sm text-neutral-600">
              {app.billing_info
                ? "Un clic pour générer la facture Stripe et envoyer l'email de bienvenue avec le lien de paiement. Note : ce bouton n'est plus nécessaire — la facture est désormais générée automatiquement dès que le candidat soumet ses infos de facturation."
                : "Dès que le candidat aura soumis ses informations de facturation à l'étape 2, la facture Stripe sera générée automatiquement et le lien de paiement lui sera envoyé par email. Pas besoin de cliquer ici."}
            </p>
            <button onClick={validateAndInvoice} disabled={generating || !app.billing_info || feeForm.final_amount <= 0}
                    className="mt-4 inline-flex items-center gap-2 rounded-full bg-wine-500 px-6 py-3 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-50"
                    data-testid="validate-and-invoice">
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
              Forcer la génération de la facture (override manuel)
            </button>
            {!app.billing_info && (
              <div className="mt-3 text-xs text-amber-700">⏳ En attente de la soumission du formulaire de facturation par le candidat.</div>
            )}
          </>
        )}
      </section>

      {/* STAGE 4 — Membre actif (auto post-paiement) */}
      <section id="stage-4" className="mt-6 rounded-2xl border border-wine-100 bg-white p-6" data-testid="stage-4">
        <StageHeader id={4} label="Membre actif" icon={UserCheck} active={stage === 4} complete={app.status === "active"} />

        {app.status === "paid" || app.status === "onboarding_scheduled" || app.status === "active" ? (
          <div className="mt-5 space-y-4">
            <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 p-4">
              <div className="text-sm font-bold text-emerald-800">✅ Paiement reçu — le candidat a été automatiquement converti en membre.</div>
              <div className="mt-1 text-xs text-emerald-700">Son compte membre est actif. Il accède désormais à tous les onglets et reçoit l&apos;email d&apos;invitation à compléter son <strong>Profil Entreprise</strong>, inviter ses collaborateurs et découvrir la FAQ.</div>
              {app.activation_email_sent_at && (
                <div className="mt-2 inline-flex items-center gap-1.5 rounded-full bg-emerald-100 px-2.5 py-0.5 text-[11px] font-bold text-emerald-800" data-testid="activation-email-sent">
                  🥂 Email « RDV avec Audrey » envoyé le {new Date(app.activation_email_sent_at).toLocaleString("fr")}
                </div>
              )}
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              <div><label className="text-[10px] font-semibold uppercase text-neutral-500">Account manager dédié</label><input value={onboarding.assigned_manager_name} onChange={(e) => setOnboarding({ ...onboarding, assigned_manager_name: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="manager-name" /></div>
              <div><label className="text-[10px] font-semibold uppercase text-neutral-500">Date d&apos;onboarding (rendez-vous)</label><input type="datetime-local" value={onboarding.scheduled_at} onChange={(e) => setOnboarding({ ...onboarding, scheduled_at: e.target.value })} className="mt-1 w-full rounded-xl border border-neutral-200 px-3 py-2 text-sm" data-testid="onboarding-date" /></div>
            </div>
            <div className="flex gap-2">
              <button onClick={() => saveOnboarding(false)} className="rounded-full bg-wine-50 px-4 py-2 text-xs font-semibold text-wine-700 hover:bg-wine-100" data-testid="schedule-onboarding">Planifier RDV onboarding</button>
              <button onClick={() => saveOnboarding(true)} disabled={app.status === "active"} className="rounded-full bg-emerald-500 px-4 py-2 text-xs font-semibold text-white hover:bg-emerald-600 disabled:opacity-50" data-testid="activate-member">Marquer membre actif</button>
            </div>
          </div>
        ) : (
          <p className="mt-4 text-sm text-neutral-500 italic">En attente du paiement (étape 3). Une fois reçu, la conversion en membre se fera automatiquement.</p>
        )}
      </section>
    </PortalLayout>
  );
}
